import type { Ref } from "vue";

// Recherche debouncée générique partagée web/mobile : enchaîne un fetcher fourni par l'appelant,
// annule les requêtes périmées (versioning) et n'interroge qu'au-delà d'une longueur minimale.
export interface UseDebouncedSearchOptions {
  minLength?: number;
  debounceMs?: number;
}

export interface UseDebouncedSearchReturn<T> {
  results: Ref<T[]>;
  loading: Ref<boolean>;
  search: (q: string) => void;
  clear: () => void;
}

export function useAppDebouncedSearch<T>(
  fetcher: (q: string) => Promise<T[]>,
  options: UseDebouncedSearchOptions = {}
): UseDebouncedSearchReturn<T> {
  const minLength = options.minLength ?? 2;
  const debounceMs = options.debounceMs ?? 250;

  const results = ref<T[]>([]) as Ref<T[]>;
  const loading = ref(false);

  let version = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  function clear() {
    clearTimeout(debounceTimer);
    ++version;
    results.value = [];
    loading.value = false;
  }

  function search(q: string) {
    clearTimeout(debounceTimer);
    if (q.trim().length < minLength) {
      clear();
      return;
    }
    const currentVersion = ++version;
    loading.value = true;
    debounceTimer = setTimeout(async () => {
      try {
        const res = await fetcher(q);
        if (currentVersion === version) results.value = res;
      } catch {
        if (currentVersion === version) results.value = [];
      } finally {
        if (currentVersion === version) loading.value = false;
      }
    }, debounceMs);
  }

  return { results, loading, search, clear };
}
